import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { ToastProvider } from "./components/ToastProvider";
import { isDashboard } from "./utils/domain";
import Navbar from "./components/Navbar";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import FeatureLandingPage from "./pages/FeatureLandingPage";
import DashboardPage from "./pages/DashboardPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import OverallAnalyticsPage from "./pages/OverallAnalyticsPage";
import AdvancedABTestingPage from "./pages/AdvancedABTestingPage";
import AdvancedABTestingEditor from "./pages/AdvancedABTestingEditor";
import AdvancedGeoTargetingPage from "./pages/AdvancedGeoTargetingPage";
import AdvancedGeoTargetingEditor from "./pages/AdvancedGeoTargetingEditor";
import AdvancedPixelsPage from "./pages/AdvancedPixelsPage";
import AdvancedPixelsEditor from "./pages/AdvancedPixelsEditor";
import AdvancedDomainsPage from "./pages/AdvancedDomainsPage";
import AdvancedConversionsPage from "./pages/AdvancedConversionsPage";
import AdvancedConversionsEditor from "./pages/AdvancedConversionsEditor";
import BioPagesList from "./pages/BioPagesList";
import BioPageEditor from "./pages/BioPageEditor";
import BioPageAnalytics from "./pages/BioPageAnalytics";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignDetailsPage from "./pages/CampaignDetailsPage";
import LinkEditPage from "./pages/LinkEditPage";
import "./App.css";

function App() {
  const { user, loading, login, register, logout } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner" />
        <p>Loading LinkSquire...</p>
      </div>
    );
  }

  // Main site (linksquire.com) — show landing page for all visitors
  if (!isDashboard) {
    return (
      <ToastProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage user={user} />} />
            <Route path="/features/:featureSlug" element={<FeatureLandingPage user={user} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ToastProvider>
    );
  }

  // Dashboard sites (dash.linksquire.com, dash.sqir.link) — require auth
  if (!user) {
    return (
      <ToastProvider>
        <AuthPage onLogin={login} onRegister={register} />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <Router>
        <div className="app">
          <Navbar user={user} onLogout={logout} />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/campaigns/:id" element={<CampaignDetailsPage />} />
              <Route path="/links/:id/edit" element={<LinkEditPage />} />
              <Route path="/bio" element={<BioPagesList />} />
              <Route path="/bio/new" element={<BioPageEditor />} />
              <Route path="/bio/:id/edit" element={<BioPageEditor />} />
              <Route path="/bio/:id/analytics" element={<BioPageAnalytics />} />
              <Route path="/analytics" element={<OverallAnalyticsPage />} />
            <Route path="/analytics/:id" element={<AnalyticsPage />} />
            <Route path="/advanced" element={<Navigate to="/advanced/ab-testing" replace />} />
            <Route path="/advanced/ab-testing" element={<AdvancedABTestingPage />} />
            <Route path="/advanced/ab-testing/:id" element={<AdvancedABTestingEditor />} />
            <Route path="/advanced/geo-targeting" element={<AdvancedGeoTargetingPage />} />
            <Route path="/advanced/geo-targeting/:id" element={<AdvancedGeoTargetingEditor />} />
            <Route path="/advanced/pixels" element={<AdvancedPixelsPage />} />
            <Route path="/advanced/pixels/:id" element={<AdvancedPixelsEditor />} />
            <Route path="/advanced/domains" element={<AdvancedDomainsPage />} />
            <Route path="/advanced/conversions" element={<AdvancedConversionsPage />} />
            <Route path="/advanced/conversions/:id" element={<AdvancedConversionsEditor />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </main>
        </div>
      </Router>
    </ToastProvider>
  );
}

export default App;
